import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { CallEntity } from './call.entity';
import { UserEntity } from './user.entity';

/**
 * Records the first time a user acknowledged (saw) a MISSED call in the
 * Communications Panel. Sibling of `SoftphoneCallListenEventEntity`
 * (which tracks voicemail playback) — together they let each of the three
 * Communications-dot contributors clear independently:
 *
 *   - voicemail  → clears when the rep PLAYS it     (listen-event)
 *   - chat       → clears when the rep OPENS it     (chat store)
 *   - missed call→ clears when the rep SEES it here  (THIS table)
 *
 * Before this existed, missed calls had no acknowledgement, so the red
 * dot — `unlistenedVoicemails + missedCalls + unreadChats` — could never
 * drop to zero once a rep had a single missed call: the count only grew.
 * A missed call counts as "unseen" by user X when no row exists here for
 * `(fkCallId, fkUserId)`; opening the Communications panel marks the rep's
 * current unseen misses as seen.
 *
 * Per-user (not tenant-wide) so a manager + rep each track their own seen
 * state — mirrors the listen-event's per-user model. Idempotent: the
 * unique index makes a re-mark a no-op. Append-only by intent (a future
 * "mark as unseen" affordance would DELETE the row, not mutate it).
 */
@Index('softphone_call_seen_user_call_uidx', ['fkUserId', 'fkCallId'], {
	unique: true,
})
@Index('softphone_call_seen_user_idx', ['fkUserId'])
@Entity('softphone_call_seen_event')
export class SoftphoneCallSeenEventEntity extends BaseEntity {
	@Column('int', { name: 'fk_call__id', unsigned: true })
	public fkCallId!: number;

	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	/**
	 * Timestamp of the first acknowledgement. Distinct from
	 * BaseEntity.createdDate (also set on insert) so a future
	 * "mark unseen then re-see" flow can update this without losing the
	 * original createdDate audit trail.
	 */
	@Column('datetime', {
		name: 'seenAt',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public seenAt!: Date;

	@ManyToOne(() => CallEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_call__id', referencedColumnName: 'id' }])
	public fkCall!: CallEntity;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;
}
